import React, { ChangeEvent, forwardRef } from "react";
import classnames from "classnames";
import PhoneInputComponent, {
  PhoneNumber,
  PhoneInputProps,
} from "mui-phone-input";
import makeStyles from "@mui/styles/makeStyles";
import InputLabel from "@mui/material/InputLabel";
import { styles } from "./styles";
import { InputHelpText } from "../InputHelpText";

type Props = {
  id?: string;
  name?: string;
  size?: "small" | "medium";
  className?: string;
  label?: string;
  disabled?: boolean;
  labelComponent?: React.ComponentType;
  description?: string;
  fullWidth?: boolean;
  error?: string | boolean;
  defaultValue?: string;
  value?: PhoneNumber | string;
  required?: boolean;
  autoFocus?: boolean;
  onChange?: (value: PhoneNumber, event: ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  onInputLabelClick?(event: React.MouseEvent): void;
  inputFocused?: boolean;
  descriptionClassName?: string;
} & PhoneInputProps;

const useStyles = makeStyles(styles);

const PhoneInput = forwardRef<HTMLDivElement, Props>((props, ref) => {
  const {
    id,
    className,
    description,
    size = "medium",
    fullWidth = false,
    error = false,
    defaultValue,
    required = false,
    autoFocus = false,
    label,
    labelComponent: LabelComponent,
    onChange,
    placeholder,
    value,
    onInputLabelClick,
    inputFocused = false,
    disabled = false,
    descriptionClassName,
    ...rest
  } = props;

  const classes = useStyles(props);

  const rootClassName = classnames(
    {
      [classes.fullWidth]: fullWidth,
      [classes.error]: error,
      [classes.value]: value,
    },
    classes.root,
    classes[size],
    className,
  );

  return (
    <div className={classes.container}>
      <InputLabel
        htmlFor={id}
        onClick={onInputLabelClick}
        className={classes.inputLabel}
      >
        <div className={classes.label}>
          {label && required ? `${label}*` : label}
          {LabelComponent && <LabelComponent />}
        </div>
        <PhoneInputComponent
          id={id}
          ref={ref}
          classes={{
            root: classnames(rootClassName, {
              [classes.inputFocused]: inputFocused,
              [classes.disabled]: disabled,
            }),
          }}
          autoFocus={autoFocus}
          fullWidth={fullWidth}
          defaultValue={defaultValue}
          disabled={disabled}
          error={!!error}
          required={required}
          onChange={onChange}
          placeholder={placeholder}
          value={value}
          variant="outlined"
          // eslint-disable-next-line i18next/no-literal-string
          searchVariant="outlined"
          size={size}
          {...rest}
        />
      </InputLabel>
      <InputHelpText className={descriptionClassName} isError={!!error}>
        {error || description}
      </InputHelpText>
    </div>
  );
});

export default PhoneInput;
